import { RequireScope } from "@/components/require-scope";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/HoverCard";
import { RadioCard, RadioCardGroup } from "@/components/ui/RadioCard";
import { Text } from "@/components/ui/Text";
import { useOrganization } from "@/contexts/Auth";
import { useRBAC } from "@/hooks/useRBAC";
import { cn } from "@/lib/utils";
import { useRoutes } from "@/routes";
import { useCreateRemoteMcpServerHeaderMutation } from "@gram/client/react-query/createRemoteMcpServerHeader.js";
import { useDeleteRemoteMcpServerHeaderMutation } from "@gram/client/react-query/deleteRemoteMcpServerHeader.js";
import { useDetachUserSessionIssuerMutation } from "@gram/client/react-query/detachUserSessionIssuer.js";
import { useUserSessionIssuer } from "@gram/client/react-query/userSessionIssuer.js";
import { invalidateAllRemoteSessionClients } from "@gram/client/react-query/remoteSessionClients.js";
import {
  invalidateAllServerClientAssociations,
  useServerClientAssociations,
} from "@gram/client/react-query/serverClientAssociations.js";
import { useGetRemoteMcpServer } from "@gram/client/react-query/getRemoteMcpServer.js";
import {
  invalidateAllGetRemoteMcpServerScopes,
  useGetRemoteMcpServerScopes,
} from "@gram/client/react-query/getRemoteMcpServerScopes.js";
import { useMcpServers } from "@gram/client/react-query/mcpServers.js";
import type { GetServerIdentityImpactRequest } from "@gram/client/models/operations/getserveridentityimpact.js";
import {
  invalidateAllRemoteMcpServerHeaders,
  useRemoteMcpServerHeaders,
} from "@gram/client/react-query/remoteMcpServerHeaders.js";
import { useUpdateRemoteMcpServerHeaderMutation } from "@gram/client/react-query/updateRemoteMcpServerHeader.js";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Loader2, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import {
  FooterSaveButton,
  SettingsSection,
} from "@/components/detail/settings-section";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/Collapsible";
import { HeadersSection } from "../HeadersSection";
import { useCatalogHeaderSuggestions } from "../useCatalogHeaderSuggestions";
import { useHeaderDrafts } from "@/lib/remote-identity";
import { identityModeCards } from "@/lib/remote-identity";
import { useAgentCredentialDraft } from "@/lib/remote-identity";
import { RequestedScopesSummary } from "./RequestedScopesSummary";
import { ResourceScopePinField } from "./ResourceScopePinField";
import { useResourceScopePin } from "./resourceScopePin";
import { RepointedServersNotice } from "./RepointedServersNotice";
import type { AuthTarget } from "./authTarget";
import {
  deriveIdentityMode,
  managedAuthorizationHeader,
  findPassThroughAuthorizationHeader,
  findStaticAuthorizationHeader,
  type IdentityMode,
} from "@/lib/remote-identity";
import { useAllRemoteSessionClients } from "@/lib/remote-identity";
import { useUpstreamProbe } from "@/lib/remote-identity";
import { UserIdentityRow } from "@/lib/remote-identity";
import { useUserIdentityDraft } from "@/lib/remote-identity";
import {
  isOAuthFormat,
  oauthFormatOf,
  ServiceAccountPanel,
  useCustomerManagedKeys,
  useServiceAccountClientDraft,
  type ServiceAccountFormat,
} from "@/lib/remote-identity";
import {
  invalidateSharedIssuerImpact,
  needsSharedIssuerConfirm,
  sharedIssuerChangeBlocked,
  useSharedIssuerImpact,
} from "@/lib/remote-identity/model/upstreamRepointing";

export function RemoteMcpIdentitySectionBody({
  target,
}: {
  target: AuthTarget;
}): JSX.Element {
  const remoteMcpServerId = target.remoteMcpServerId ?? "";
  const queryClient = useQueryClient();
  const routes = useRoutes();
  const organization = useOrganization();
  const { hasScope, isLoading: rbacLoading } = useRBAC();
  const canWrite =
    !rbacLoading && hasScope("mcp:write", target.permissionResourceId);
  const organizationAdmin =
    !rbacLoading && hasScope("org:admin", organization.id);
  const headersQuery = useRemoteMcpServerHeaders(
    { remoteMcpServerId },
    undefined,
    { enabled: remoteMcpServerId !== "", throwOnError: false },
  );
  const {
    items: clients,
    isLoading: clientsLoading,
    isError: clientsError,
    error: clientsQueryError,
  } = useAllRemoteSessionClients(
    { userSessionIssuerId: target.userSessionIssuerId ?? undefined },
    { enabled: !!target.userSessionIssuerId },
  );
  const {
    data: userSessionIssuer,
    isLoading: issuerLoading,
    isError: issuerError,
  } = useUserSessionIssuer(
    { id: target.userSessionIssuerId ?? undefined },
    undefined,
    { enabled: !!target.userSessionIssuerId, throwOnError: false },
  );
  // On an organization-level issuer, a binding to an organization-owned
  // client is shared by every project's servers on that issuer, so the server
  // refuses to replace it without organization admin
  // (ErrIdentityOrgWideBinding). Project-owned clients stay editable, so only
  // that case locks the panel, and only for someone who is not an admin.
  const organizationIssuer = userSessionIssuer?.projectId === "";
  const orgSharedClient = organizationIssuer
    ? clients.find((linked) => linked.projectId === "")
    : undefined;
  const orgSharedClientLocked = !!orgSharedClient && !organizationAdmin;
  const sourceQuery = useGetRemoteMcpServer(
    { id: remoteMcpServerId },
    undefined,
    {
      enabled: remoteMcpServerId !== "",
    },
  );
  const siblingsQuery = useMcpServers({ remoteMcpServerId }, undefined, {
    enabled: remoteMcpServerId !== "",
  });
  const linkedServers = (siblingsQuery.data?.mcpServers ?? []).filter(
    (server) => server.remoteMcpServerId === remoteMcpServerId,
  );
  const sharedSource = linkedServers.length > 1;
  // Other servers backed by the same remote source. Their headers are these
  // rows, and #6524 removed the remote's own page, so this is the only place
  // to edit them: the change is named rather than locked.
  const siblingMcpServers = linkedServers.filter(
    (server) => server.id !== target.permissionResourceId,
  );
  // Identity stays locked on a shared source — one server must not repoint the
  // binding for its siblings — but headers do not inherit that lock.
  const headersReadOnly =
    siblingsQuery.isLoading || siblingsQuery.isError || !canWrite;
  // Held until the issuer is classified: an organization-wide one could make
  // Save fail with ErrIdentityOrgWideBinding, so neither a pending nor a
  // failed lookup may leave the controls open.
  const identityReadOnly =
    sharedSource ||
    orgSharedClientLocked ||
    issuerLoading ||
    issuerError ||
    siblingsQuery.isLoading ||
    siblingsQuery.isError ||
    !canWrite;
  const headers = headersQuery.data?.headers ?? [];
  const authorizationHeader = findStaticAuthorizationHeader(headers);
  const passThroughAuthorization = findPassThroughAuthorizationHeader(headers);
  const identityQueryError = headersQuery.isError || clientsError;
  // A service account client (credential_owner self) is bound the same way as
  // a per-user one, but it is the Service Account choice: one credential every
  // caller shares, obtained by the client rather than pasted as a header.
  const selfClient = clients.find(
    (linked) => linked.credentialOwner === "self",
  );
  const subjectClients = clients.filter(
    (linked) => linked.credentialOwner !== "self",
  );
  const actualMode = identityQueryError
    ? null
    : selfClient
      ? "agent"
      : deriveIdentityMode(subjectClients.length, headers);
  const loading =
    headersQuery.isLoading || clientsLoading || siblingsQuery.isLoading;
  const identityResolved = !loading && !identityQueryError;
  const [selectedMode, setSelectedMode] = useState<IdentityMode>("none");
  // Raised by Save when it would remove an existing identity, which is the
  // "after confirmation" AIM-230 asks for.
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (actualMode) setSelectedMode(actualMode);
  }, [actualMode]);

  // The header rows are part of this panel's one commit, so their state lives
  // here rather than inside the disclosure that renders them.
  const headerSuggestions = useCatalogHeaderSuggestions(
    remoteMcpServerId,
    !!headersQuery.data && headers.length === 0 && !identityReadOnly,
  );
  const headerDrafts = useHeaderDrafts({
    remoteMcpServerId,
    identity: {
      mode: selectedMode,
      managed: managedAuthorizationHeader(selectedMode, headers),
      isError: !!identityQueryError,
    },
    readOnly: headersReadOnly,
    suggestions: headerSuggestions,
  });

  const noneProbeStatus = useUpstreamProbe(
    remoteMcpServerId,
    identityResolved &&
      actualMode === "none" &&
      selectedMode === "none" &&
      !passThroughAuthorization,
  );
  const createHeader = useCreateRemoteMcpServerHeaderMutation();
  const updateHeader = useUpdateRemoteMcpServerHeaderMutation();
  const deleteHeader = useDeleteRemoteMcpServerHeaderMutation();
  const saving =
    createHeader.isPending || updateHeader.isPending || deleteHeader.isPending;

  const invalidateHeaders = async () => {
    await invalidateAllRemoteMcpServerHeaders(queryClient, {
      refetchType: "all",
    });
    const refreshed = await headersQuery.refetch();
    return !refreshed.isError && !!refreshed.data;
  };

  // The upstream this server fronts, by name — the identity copy is written
  // about the service, not about "the upstream server".
  const upstreamName =
    linkedServers[0]?.name?.trim() ||
    sourceQuery.data?.slug ||
    "the upstream service";

  const userDraft = useUserIdentityDraft({
    mcpServerId: target.permissionResourceId,
    remoteMcpServerId,
    upstreamUrl: sourceQuery.data?.url,
    linkedClients: subjectClients,
    configured: actualMode === "user",
    enabled: identityResolved && selectedMode === "user",
  });
  // mcp:write satisfies mcp:read, but a read block applies independently.
  const canRead =
    !rbacLoading && hasScope("mcp:read", target.permissionResourceId);
  const associationsQuery = useServerClientAssociations(
    { mcpServerId: target.permissionResourceId },
    undefined,
    {
      enabled: canRead && identityResolved && selectedMode === "user",
      throwOnError: false,
    },
  );
  // Other servers using this server's client: rotating or replacing the
  // client changes their sign-in too.
  const connectedAssociation = associationsQuery.data?.associations.find(
    (association) =>
      association.remoteSessionClientId === userDraft.connectedClientId,
  );
  const otherServersOnClient = connectedAssociation
    ? connectedAssociation.serverCount - 1
    : 0;
  const agentDraft = useAgentCredentialDraft({
    remoteMcpServerId,
    authorizationHeader,
    onSaved: invalidateHeaders,
    createHeader,
    updateHeader,
  });
  const clientDraft = useServiceAccountClientDraft({
    mcpServerId: target.permissionResourceId,
    boundClient: selfClient,
    enabled: selectedMode === "agent",
  });
  const { entitled: customerManagedKeys } = useCustomerManagedKeys();
  const [formatPick, setFormatPick] = useState<ServiceAccountFormat | null>(
    null,
  );
  const saFormat: ServiceAccountFormat =
    formatPick ??
    (selfClient
      ? oauthFormatOf(selfClient)
      : agentDraft.format === "client-credentials"
        ? "bearer"
        : agentDraft.format);
  const oauthFormat =
    selectedMode === "agent" && isOAuthFormat(saFormat) ? saFormat : null;
  const oauthSelected = oauthFormat !== null;

  // The pin belongs to the server's protected resource; the panel shows it to
  // writers with a bound client, and scopes.canPin says if they may change it.
  const scopePin = useResourceScopePin({
    mcpServerId: target.permissionResourceId,
    enabled: canWrite && identityResolved && actualMode === "user",
  });
  const scopePinSlot =
    canWrite && selectedMode === "user" && userDraft.connected;
  // Pinning is off for the org, so the picker can do nothing.
  const scopePinUnusable = !!scopePin.data && !scopePin.data.discoveryEnabled;
  const showScopePin = scopePinSlot && !!scopePin.data && !scopePinUnusable;
  const scopePinDirty = showScopePin && scopePin.dirty;
  // Writers get the same answer in the pin's status line when the pin shows.
  const showScopesSummary =
    canRead &&
    (!scopePinSlot || scopePinUnusable) &&
    identityResolved &&
    actualMode === "user" &&
    selectedMode === "user" &&
    userDraft.connectedClientId !== null;
  const scopesSummaryQuery = useGetRemoteMcpServerScopes(
    { mcpServerId: target.permissionResourceId },
    undefined,
    {
      enabled: showScopesSummary && target.permissionResourceId !== "",
      throwOnError: false,
    },
  );

  const detachIssuer = useDetachUserSessionIssuerMutation();

  // Picking a card only changes the draft. Nothing is written, removed or
  // confirmed until Save — the panel has one commit point and this is it.
  const handleModeChange = (next: string) => {
    if (identityReadOnly || passThroughAuthorization) return;
    setSelectedMode(next as IdentityMode);
  };

  // What leaving the current mode would destroy. Identity is derived, so
  // leaving User means unbinding the client and leaving Agent means deleting
  // the credential; both are what Save has to do, not the card click.
  const leavingUser = actualMode === "user" && selectedMode !== "user";
  // An OAuth client takes a static credential's place rather than sitting
  // beside it, so the confirmation says replace, not remove. Only a client
  // Save binds, or one already bound, does: an unfinished form replaces
  // nothing.
  const replacingCredential =
    !!oauthFormat &&
    !!authorizationHeader &&
    (clientDraft.canSave(oauthFormat) || clientDraft.showingBound(oauthFormat));
  // A server can carry a bound client and a leftover static credential at
  // once, and the client wins the derived mode. No Identity has to clear both
  // or the credential alone would make the server read as Agent again — and
  // unlike under User, nothing overrides it on the way upstream. Under a bound
  // service account client, only a leftover credential is left behind.
  const leavingAgent =
    (actualMode === "agent" &&
      selectedMode !== "agent" &&
      (!selfClient || !!authorizationHeader)) ||
    (selectedMode === "none" && !!authorizationHeader) ||
    replacingCredential;
  // Moving off the service account client to a static credential or to no
  // identity unbinds it. Another OAuth client, or User Identity, replaces it
  // in the same commit that binds the new one.
  const leavingSelf =
    !!selfClient &&
    (selectedMode === "none" || (selectedMode === "agent" && !oauthSelected));
  // No Identity removes the client with nothing in its place.
  const removingSelf = leavingSelf && selectedMode === "none";
  // Staying on User but saving a different client swaps out the one people
  // signed in through, so they all have to sign in again.
  const replacingClient = selectedMode === "user" && userDraft.replacesClient;
  // Another OAuth client, or User Identity, takes the service account
  // client's place: the commit that binds the new client unbinds it.
  const replacingSelf =
    leavingSelf ||
    (!!selfClient &&
      (selectedMode === "user" ||
        (!!oauthFormat && clientDraft.canSave(oauthFormat))));
  const destructive =
    leavingUser || leavingAgent || replacingSelf || replacingClient;

  // The upstream authorization server is derived per user session issuer, so
  // changing this server's clients can re-point every server sharing the
  // issuer, in any project for an organization issuer.
  const savingUserIdentity = selectedMode === "user" && userDraft.canSave;
  const sharedIssuerId = target.userSessionIssuerId;
  let impactRequest: GetServerIdentityImpactRequest | null = null;
  if (sharedIssuerId && leavingUser) {
    impactRequest = {
      userSessionIssuerId: sharedIssuerId,
      mcpServerId: target.mcpServerId,
      change: "detach",
    };
  } else if (sharedIssuerId && savingUserIdentity && userDraft.selected) {
    impactRequest = {
      userSessionIssuerId: sharedIssuerId,
      mcpServerId: target.mcpServerId,
      change: "replace",
      providerId: userDraft.selected.isNew ? undefined : userDraft.selected.id,
      clientId:
        userDraft.choice === "existing"
          ? (userDraft.existingClientId ?? undefined)
          : undefined,
    };
  }
  const sharedIssuerImpact = useSharedIssuerImpact(impactRequest);
  const repointLookupPending = sharedIssuerImpact.pending;
  const needsConfirm =
    destructive || needsSharedIssuerConfirm(sharedIssuerImpact);

  const detachUserIdentity = async (): Promise<boolean> => {
    const userSessionIssuerId = target.userSessionIssuerId;
    if (!userSessionIssuerId) return false;
    for (const linked of clients) {
      await detachIssuer.mutateAsync({
        // The generated detach request reuses the attach form's field name;
        // the endpoint it posts to is what distinguishes them.
        request: {
          attachUserSessionIssuerForm: {
            id: linked.id,
            userSessionIssuerId,
          },
        },
      });
    }
    await Promise.all([
      invalidateAllRemoteSessionClients(queryClient),
      invalidateAllServerClientAssociations(queryClient),
      invalidateAllGetRemoteMcpServerScopes(queryClient),
      invalidateSharedIssuerImpact(queryClient),
    ]);
    return true;
  };

  const removeAgentCredential = async (): Promise<boolean> => {
    if (!authorizationHeader) return false;
    // Under No Identity the row is editable in Custom Headers. When the draft
    // has already dropped it, the header save deletes it; deleting it here
    // first would fail that save on a row that is gone and strand the rest.
    const headerId = authorizationHeader.id;
    if (!headerDrafts.drafts.some((draft) => draft.id === headerId)) {
      return false;
    }
    await deleteHeader.mutateAsync({ request: { id: headerId } });
    const refreshed = await invalidateHeaders();
    if (!refreshed) {
      toast.warning("Credential removed, but headers could not be refreshed.");
    }
    return true;
  };

  // The Authorization row is managed while Service Account is selected, so
  // it never reaches the header drafts and has to be deleted directly.
  const deleteStaticCredential = async (): Promise<void> => {
    if (!authorizationHeader) return;
    await deleteHeader.mutateAsync({
      request: { id: authorizationHeader.id },
    });
    await invalidateHeaders();
  };

  const detachSelfClient = async (): Promise<void> => {
    const userSessionIssuerId = target.userSessionIssuerId;
    if (!selfClient || !userSessionIssuerId) return;
    await detachIssuer.mutateAsync({
      request: {
        attachUserSessionIssuerForm: { id: selfClient.id, userSessionIssuerId },
      },
    });
    await Promise.all([
      invalidateAllRemoteSessionClients(queryClient),
      invalidateAllServerClientAssociations(queryClient),
    ]);
  };

  // Switching to User commits a client; without one, removing the old
  // identity first would leave the server with none.
  const userSwitchBlocked =
    selectedMode === "user" && actualMode !== "user" && !userDraft.canSave;
  // An unfinished identity edit holds the whole commit, or Save would write
  // the rest and silently drop it.
  const userEditIncomplete =
    selectedMode === "user" &&
    actualMode === "user" &&
    userDraft.pendingChange &&
    !userDraft.canSave;
  // Leaving the service account client for a static credential unbinds it
  // only once a credential is written. With none typed and none on the
  // server, Save would skip the identity half and commit the rest.
  const staticSwitchBlocked =
    !!selfClient &&
    selectedMode === "agent" &&
    !oauthSelected &&
    !agentDraft.canSave &&
    !authorizationHeader;
  // Leaving User for a service account client keeps the user client until
  // the new one binds. Without one to bind, Save would commit the rest and
  // leave the server on User Identity behind the operator's back.
  const oauthSwitchBlocked =
    leavingUser && !!oauthFormat && !clientDraft.canSave(oauthFormat);

  const performSave = async () => {
    if (
      !canWrite ||
      rbacLoading ||
      userSwitchBlocked ||
      userEditIncomplete ||
      staticSwitchBlocked ||
      oauthSwitchBlocked
    )
      return;
    setConfirmOpen(false);
    // The draft is reset at most once per save: a reset deletes a created
    // signing key client, and a second one would delete it again.
    let draftReset = false;
    try {
      // Binding a service account client replaces the user clients in the
      // same commit, so they are only detached here when nothing is bound.
      if (leavingUser && !oauthFormat) await detachUserIdentity();
      // Leaving for a static credential unbinds the client only once that
      // credential is written, below.
      if (removingSelf) {
        await detachSelfClient();
        clientDraft.reset();
        draftReset = true;
      }
      if (oauthFormat) {
        // Bind first, so a failed commit never leaves the server with no
        // identity; then drop the static credential the client replaces. An
        // unchanged bound client has nothing to bind, and a header or pin
        // edit still goes through.
        const binding = clientDraft.canSave(oauthFormat);
        if (binding && !(await clientDraft.save(oauthFormat))) return;
        if (replacingCredential) await deleteStaticCredential();
        if (binding) setFormatPick(null);
      } else if (selectedMode === "user" && leavingAgent) {
        // Bind the client before dropping the credential, so a failed commit
        // never leaves the server with no identity; the client outranks the
        // header while both exist.
        if (!(await userDraft.save())) return;
        await removeAgentCredential();
      } else {
        if (leavingAgent) await removeAgentCredential();
        if (selectedMode === "agent") {
          const written = await agentDraft.save();
          if (leavingSelf) {
            // A failed write has already said so; stop there. Otherwise
            // unbind only with a credential to fall back on, new or already
            // on the server, or the server is left with no identity.
            if (agentDraft.canSave && !written) return;
            if (written || authorizationHeader) {
              await detachSelfClient();
              clientDraft.reset();
              draftReset = true;
            }
          }
        } else if (
          selectedMode === "user" &&
          (actualMode !== "user" || userDraft.canSave)
        ) {
          // A pin or header edit alone must not recommit the connected client.
          await userDraft.save();
        }
      }
      // A signing key client made for the Service Account choice and left
      // behind by a save that chose otherwise is never bound, so it goes.
      if (!oauthFormat && clientDraft.created && !draftReset) {
        clientDraft.reset();
      }
    } catch (error) {
      toast.error(errorMessage(error, "Failed to save identity"));
      return;
    }
    // Headers after identity: it may have just written or removed the
    // Authorization row, and these rows are diffed against what the server
    // holds once that has landed. The pin is independent of both.
    const [pinResult, headersResult] = await Promise.allSettled([
      scopePinDirty ? scopePin.save() : Promise.resolve(false),
      headerDrafts.save(),
    ]);
    if (pinResult.status === "rejected") {
      toast.error(
        errorMessage(pinResult.reason, "Failed to save pinned scopes"),
      );
    } else if (pinResult.value) {
      toast.success("Pinned scopes updated");
    }
    if (headersResult.status === "rejected") {
      toast.error(errorMessage(headersResult.reason, "Failed to save headers"));
      return;
    }
    // Reported only once the headers have landed: when the draft already
    // dropped the credential, the header save is what deletes it, and a
    // failure there must not follow a claim that it is gone.
    if (selectedMode === "none" && destructive) {
      toast.success("Identity removed");
    }
    if (headersResult.value) {
      toast.success("Upstream headers updated");
    }
  };

  const identityError = headersQuery.error ?? clientsQueryError;
  // A server that answers with a challenge has no business on No Identity, so
  // that card says so rather than a banner underneath the choice.
  const noneWarning =
    noneProbeStatus === "authentication-required"
      ? "This server answers with an authentication challenge. With no identity configured, requests to it will keep failing — choose User Identity or a shared credential."
      : null;
  const cards = identityModeCards(upstreamName);

  // One Save for the whole section. What it commits depends on the selected
  // mode, and it disappears into a disabled state when there is nothing to do
  // rather than sprouting a button per sub-form.
  let identityCanSave: boolean;
  if (selectedMode === "user") {
    identityCanSave = userDraft.canSave;
  } else if (oauthFormat) {
    identityCanSave = clientDraft.canSave(oauthFormat);
  } else if (selectedMode === "agent") {
    // Moving to Agent needs a credential; without one there is nothing for
    // the mode to actually be. One already on the server counts: in the
    // legacy state where a bound client outranks a static header, detaching
    // the client is the whole change, and demanding a fresh secret would ask
    // the operator to retype one they cannot read.
    identityCanSave =
      agentDraft.canSave ||
      ((leavingUser || leavingSelf) && !!authorizationHeader);
  } else {
    // No Identity commits only the removal it implies.
    identityCanSave = destructive;
  }
  // A locked identity still lets a pin-only edit through.
  const pinOnlyChange =
    scopePinDirty && !identityCanSave && !headerDrafts.isDirty;
  // Rows that cannot be written stop the whole commit rather than letting the
  // identity half through and dropping the rest on the floor.
  const headersBlocked =
    headerDrafts.isDirty && headerDrafts.validationError !== null;
  const headersReason = headersBlocked && headerDrafts.reportErrors;
  const canSave =
    !headersBlocked &&
    !userSwitchBlocked &&
    !userEditIncomplete &&
    !staticSwitchBlocked &&
    !oauthSwitchBlocked &&
    (identityCanSave || headerDrafts.isDirty || scopePinDirty);
  const savePending =
    detachIssuer.isPending ||
    scopePin.saving ||
    saving ||
    headerDrafts.saving ||
    (selectedMode === "user"
      ? userDraft.saving
      : oauthSelected
        ? clientDraft.saving
        : agentDraft.saving);

  return (
    <>
      <SettingsSection.Panel>
        <div className="divide-y">
          <div className="space-y-4 px-6 py-5">
            {sharedSource ? (
              <Alert variant="warning" dismissible={false}>
                This identity is shared by {linkedServers.length} MCP servers.
                Editing is disabled here so one server cannot change the
                credential used by the others. Manage linked authorization
                servers and clients in{" "}
                <Link
                  className="font-medium underline underline-offset-2"
                  to={routes.remoteIdentityProviders.href()}
                >
                  Authorization Servers
                </Link>
                .
              </Alert>
            ) : null}

            {orgSharedClient && orgSharedClientLocked && !sharedSource ? (
              <Alert variant="warning" dismissible={false}>
                This server uses a client shared by every project on the
                organization&apos;s session issuer, so it can&apos;t be changed
                here. An organization admin can remove it from this server on
                the{" "}
                <Link
                  className="font-medium underline underline-offset-2"
                  to={routes.remoteIdentityProviders.clientDetail.mcpServers.href(
                    orgSharedClient.remoteSessionIssuerId,
                    orgSharedClient.id,
                  )}
                >
                  client&apos;s MCP servers
                </Link>
                , or you can move this server to a project session issuer under
                Sessions below.
              </Alert>
            ) : null}

            {siblingsQuery.isError ? (
              <Alert variant="error" dismissible={false}>
                Could not verify whether this Remote MCP source is shared.
                Identity editing is disabled.
              </Alert>
            ) : null}

            {issuerError ? (
              <Alert variant="error" dismissible={false}>
                Could not load this server's user session issuer. Identity
                editing is disabled.
              </Alert>
            ) : null}

            {identityQueryError ? (
              <Alert variant="error" dismissible={false}>
                Could not determine the current identity configuration
                {identityError?.message ? `: ${identityError.message}` : "."}
              </Alert>
            ) : null}

            {passThroughAuthorization ? (
              <Alert variant="warning" dismissible={false}>
                A legacy pass-through Authorization header is still configured.
                Remove it in Custom Headers before selecting a shared credential
                or relying on Manual.
              </Alert>
            ) : null}

            {identityQueryError ? (
              <Text muted small>
                Identity is unavailable.
              </Text>
            ) : loading ? (
              <Text muted small>
                Loading identity…
              </Text>
            ) : (
              <RequireScope
                scope="mcp:write"
                resourceId={target.permissionResourceId}
                level="component"
                className="w-full"
              >
                {({ disabled: scopeDisabled }) => (
                  <RadioCardGroup
                    orientation="horizontal"
                    showIndicator={false}
                    value={selectedMode}
                    disabled={identityReadOnly || scopeDisabled || saving}
                    onValueChange={handleModeChange}
                    className="grid-flow-row grid-cols-1 md:grid-flow-col md:grid-cols-none"
                  >
                    {cards.map((card) => {
                      const warned = card.value === "none" && !!noneWarning;
                      return (
                        <RadioCard
                          key={card.value}
                          value={card.value}
                          // Only the pass-through conflict genuinely blocks a
                          // choice: that legacy header already occupies the
                          // Authorization name a static credential needs.
                          // Agent to User is fine — a linked client wins over
                          // a static credential, and AIM-230 expects the stale
                          // one to be left visible for cleanup.
                          disabled={
                            card.value === "agent" && !!passThroughAuthorization
                          }
                          leading={card.icon}
                          // Both states, so selecting the card does not swap
                          // the warning border back to the selected one.
                          className={cn(
                            warned &&
                              "border-warning-default has-data-[state=checked]:border-warning-default",
                          )}
                          title={
                            warned ? (
                              <span className="flex items-center gap-2">
                                {card.title}
                                <HoverCard openDelay={150}>
                                  <HoverCardTrigger asChild>
                                    <span className="text-default-warning inline-flex cursor-help">
                                      <TriangleAlert
                                        aria-label="This server requires authentication"
                                        className="size-3.5"
                                      />
                                    </span>
                                  </HoverCardTrigger>
                                  <HoverCardContent
                                    align="start"
                                    className="w-72"
                                  >
                                    <Text small className="block">
                                      {noneWarning}
                                    </Text>
                                  </HoverCardContent>
                                </HoverCard>
                              </span>
                            ) : (
                              card.title
                            )
                          }
                        >
                          {card.description}
                        </RadioCard>
                      );
                    })}
                  </RadioCardGroup>
                )}
              </RequireScope>
            )}
          </div>

          {identityResolved && selectedMode === "user" ? (
            // The provider row is the whole decision, so it takes the full
            // width rather than sitting beside a label that restates it.
            <div className="px-6 py-5">
              <UserIdentityRow
                draft={userDraft}
                disabled={identityReadOnly || userDraft.saving}
                createHref={routes.remoteIdentityProviders.href()}
                clientHref={(issuerId, clientId) =>
                  routes.remoteIdentityProviders.clientDetail.href(
                    issuerId,
                    clientId,
                  )
                }
              />
              {otherServersOnClient > 0 ? (
                <Text muted small className="mt-4 block pl-[52px]">
                  This client also signs in{" "}
                  {otherServersOnClient === 1
                    ? "1 other MCP server"
                    : `${otherServersOnClient} other MCP servers`}
                  . Changing or rotating it changes their sign-in too.
                </Text>
              ) : null}
              {showScopePin && scopePin.data ? (
                // Commits with the footer's Save, like the rest of the panel.
                <div className="mt-4 pl-[52px]">
                  <ResourceScopePinField
                    pin={scopePin}
                    scopes={scopePin.data}
                    connectedClientId={userDraft.connectedClient?.id ?? null}
                    issuerScopes={userDraft.scopeOptions}
                    serverName={
                      linkedServers
                        .find(
                          (server) => server.id === target.permissionResourceId,
                        )
                        ?.name?.trim() ?? ""
                    }
                    // Changing it needs write access to every server sharing the resource.
                    disabled={!canWrite || !scopePin.data.canPin || savePending}
                  />
                </div>
              ) : scopePinSlot && scopePin.isError ? (
                <Text muted small className="mt-4 block pl-[52px]">
                  Couldn't load pinned scopes.
                </Text>
              ) : scopePinSlot && !scopePin.data ? (
                <Text muted small className="mt-4 block pl-[52px]">
                  Loading pinned scopes…
                </Text>
              ) : null}
              {showScopesSummary && scopesSummaryQuery.data ? (
                <div className="mt-4 pl-[52px] empty:hidden">
                  <RequestedScopesSummary
                    scopes={scopesSummaryQuery.data}
                    connectedClientId={userDraft.connectedClientId}
                  />
                </div>
              ) : showScopesSummary && scopesSummaryQuery.isError ? (
                <Text muted small className="mt-4 block pl-[52px]">
                  Couldn't load requested scopes.
                </Text>
              ) : null}
            </div>
          ) : null}

          {identityResolved && selectedMode === "agent" ? (
            <div className="px-6 py-5">
              <ServiceAccountPanel
                format={saFormat}
                onFormatChange={setFormatPick}
                agentDraft={agentDraft}
                clientDraft={clientDraft}
                upstreamName={upstreamName}
                disabled={identityReadOnly || !!passThroughAuthorization}
                customerManagedKeys={customerManagedKeys}
                providersHref={routes.remoteIdentityProviders.href()}
                clientHref={(issuerId, clientId) =>
                  routes.remoteIdentityProviders.clientDetail.href(
                    issuerId,
                    clientId,
                  )
                }
              />
            </div>
          ) : null}

          <Collapsible>
            <CollapsibleTrigger className="group hover:bg-muted/40 flex w-full items-center gap-3 px-6 py-4 text-left">
              <ChevronDown
                aria-hidden="true"
                className="size-4 transition-transform group-data-[state=open]:rotate-180"
              />
              <Text small className="font-medium">
                Custom Headers
              </Text>
              <Text muted small>
                Upstream headers sent with every request.
              </Text>
              {headerDrafts.isDirty ? (
                // The rows commit with the footer's Save, so a collapsed
                // disclosure must still admit it is holding unsaved edits.
                <Text muted small className="ml-auto">
                  Unsaved
                </Text>
              ) : null}
            </CollapsibleTrigger>
            <CollapsibleContent className="px-6 pt-2 pb-6">
              <HeadersSection
                siblingMcpServers={siblingMcpServers}
                state={headerDrafts}
                resourceId={target.permissionResourceId}
              />
            </CollapsibleContent>
          </Collapsible>
        </div>

        {identityResolved &&
        (selectedMode !== "none" || destructive || headerDrafts.isDirty) ? (
          <SettingsSection.Footer>
            {headersReason ? (
              // The disclosure can be collapsed over the offending row, so the
              // reason Save is dead has to be readable from out here, in the
              // same orange the row's field is wearing. Nothing is said when
              // nothing is wrong.
              <Text small warning>
                {headerDrafts.validationError}
              </Text>
            ) : null}
            {userEditIncomplete ? (
              <Text small warning>
                Finish the User Identity change, or cancel it, to save.
              </Text>
            ) : null}
            {repointLookupPending ? (
              <Text small muted>
                Checking other servers on this user session issuer…
              </Text>
            ) : null}
            {staticSwitchBlocked ? (
              <Text small warning>
                Enter the credential, or cancel, to save.
              </Text>
            ) : null}
            {oauthSwitchBlocked ? (
              <Text small warning>
                Finish the service account client, or cancel, to save.
              </Text>
            ) : null}
            <SettingsSection.FooterActions>
              {selectedMode === "agent" && clientDraft.replacing ? (
                // Backs out of replacing the saved service account client.
                <Button
                  variant="secondary"
                  disabled={savePending}
                  onClick={() => {
                    clientDraft.cancelReplace();
                    // A format picked after Clear goes too, or Save would
                    // still unbind the client being kept.
                    setFormatPick(null);
                  }}
                >
                  <Button.Text>Cancel</Button.Text>
                </Button>
              ) : null}
              <RequireScope
                scope="mcp:write"
                resourceId={target.permissionResourceId}
                level="component"
              >
                <FooterSaveButton
                  pending={savePending}
                  disabled={
                    !canSave ||
                    savePending ||
                    (identityReadOnly && !pinOnlyChange) ||
                    repointLookupPending
                  }
                  onClick={() => {
                    if (needsConfirm) setConfirmOpen(true);
                    else void performSave();
                  }}
                />
              </RequireScope>
            </SettingsSection.FooterActions>
          </SettingsSection.Footer>
        ) : null}
      </SettingsSection.Panel>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <Dialog.Content className="max-w-md">
          <Dialog.Header>
            <Dialog.Title>
              {confirmTitle({
                removingSelf,
                replacingSelf,
                leavingUser,
                replacingClient,
                replacingCredential,
                destructive,
                upstreamName,
              })}
            </Dialog.Title>
            {destructive ? (
              <Dialog.Description>
                {removalConsequences({
                  removingSelf,
                  replacingSelf,
                  leavingUser,
                  leavingAgent,
                  replacingClient,
                  replacingCredential,
                })}
              </Dialog.Description>
            ) : (
              <Dialog.Description className="sr-only">
                Saving changes the upstream of other servers.
              </Dialog.Description>
            )}
          </Dialog.Header>
          <RepointedServersNotice
            impact={sharedIssuerImpact}
            projectId={target.projectId}
          />
          <Dialog.Footer>
            <Button
              variant="secondary"
              disabled={savePending}
              onClick={() => setConfirmOpen(false)}
            >
              <Button.Text>Cancel</Button.Text>
            </Button>
            <Button
              variant="destructive-primary"
              disabled={
                savePending ||
                !canWrite ||
                rbacLoading ||
                repointLookupPending ||
                sharedIssuerChangeBlocked(sharedIssuerImpact)
              }
              onClick={() => void performSave()}
            >
              {savePending ? (
                <Button.LeftIcon>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                </Button.LeftIcon>
              ) : null}
              <Button.Text>
                {savePending ? "Saving" : "Save changes"}
              </Button.Text>
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </>
  );
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

const UNLINK_PROVIDER_CONSEQUENCE =
  "Saving unlinks the authorization server from this server. People who already signed in lose access through it and would have to authorize again if you switch back. The authorization server and its client stay available to other servers.";
const REPLACE_CLIENT_CONSEQUENCE =
  "Saving replaces the client this server uses. Everyone signed in through the current one will have to sign in again. The old client stays available to other servers.";
const REMOVE_SELF_CLIENT_CONSEQUENCE =
  "Saving stops this server using its service account client. Callers' requests will be rejected until you save a new identity. The client stays available to other servers.";
const REPLACE_SELF_CLIENT_CONSEQUENCE =
  "Saving stops this server using its current service account client. Callers get the new credential on their next request. The old client stays available to other servers.";
const REMOVE_CREDENTIAL_CONSEQUENCE =
  "Saving removes the static Authorization credential from the Remote MCP source. Requests will no longer authenticate upstream.";
const REPLACE_CREDENTIAL_CONSEQUENCE =
  "Saving replaces the static Authorization credential with the service account client. Callers get the client's token on their next request.";

/**
 * What the confirmation discloses. A server can carry a bound client and a
 * leftover static credential at once, and No Identity removes both, so the
 * dialog has to name both.
 */
function removalConsequences({
  removingSelf,
  replacingSelf,
  leavingUser,
  leavingAgent,
  replacingClient,
  replacingCredential,
}: {
  removingSelf: boolean;
  replacingSelf: boolean;
  leavingUser: boolean;
  leavingAgent: boolean;
  replacingClient: boolean;
  replacingCredential: boolean;
}): string {
  const credentialConsequence = replacingCredential
    ? REPLACE_CREDENTIAL_CONSEQUENCE
    : REMOVE_CREDENTIAL_CONSEQUENCE;
  if (removingSelf || replacingSelf) {
    // Only another service account client takes a static credential's
    // place; No Identity and User Identity drop it.
    const selfConsequence = removingSelf
      ? REMOVE_SELF_CLIENT_CONSEQUENCE
      : REPLACE_SELF_CLIENT_CONSEQUENCE;
    return leavingAgent
      ? `${selfConsequence} ${credentialConsequence}`
      : selfConsequence;
  }
  if (replacingClient) return REPLACE_CLIENT_CONSEQUENCE;
  if (leavingUser && leavingAgent) {
    return `${UNLINK_PROVIDER_CONSEQUENCE} ${credentialConsequence}`;
  }
  if (leavingUser) return UNLINK_PROVIDER_CONSEQUENCE;
  return credentialConsequence;
}

function confirmTitle({
  removingSelf,
  replacingSelf,
  leavingUser,
  replacingClient,
  replacingCredential,
  destructive,
  upstreamName,
}: {
  removingSelf: boolean;
  replacingSelf: boolean;
  leavingUser: boolean;
  replacingClient: boolean;
  replacingCredential: boolean;
  destructive: boolean;
  upstreamName: string;
}): string {
  if (!destructive) return "Change the upstream of other servers?";
  if (removingSelf) return "Remove the service account client?";
  if (replacingSelf) return "Replace the service account client?";
  if (replacingClient) return "Replace the connected client?";
  if (leavingUser) return `Stop signing users in through ${upstreamName}?`;
  return replacingCredential
    ? "Replace the shared credential?"
    : "Remove the shared credential?";
}
